import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Helmet } from "react-helmet-async";
import { BRANDS } from "app/constants";
import { getBrandDisplayName } from "app/reducers/partnerSelector";
import { useLocation } from "react-router-dom";
import appGlobalsContext from "app/AppGlobalsContext";
import { useContext } from "react";
import { parseQueries } from "app/utils/routerUtils";
import { formatTemplate } from "app/utils/text/formatTemplate";
import { getCanonicalUrl, getDomain } from "app/utils/metas/getCanonicalUrl";

const formatHreflang = hreflang => {
	if (hreflang.endsWith("-WW")) {
		return hreflang.replace("-WW", "");
	}
	return hreflang;
};

const getHref = ({
	brand,
	domain,
	proxyBasenamePrefix = "",
	locale,
	country,
	path = "",
	queryParam = "",
}) => {
	const isPSInternational = brand === BRANDS.PS && locale !== "fr" && country !== "FR";

	return isPSInternational
		? `https://booking.perfectstay.com/${locale}-${country}${path}${queryParam}`
		: `${domain}${proxyBasenamePrefix}/${locale}-${country}${path}${queryParam}`;
};

const computeAlternates = ({
	shops = [],
	brand,
	domain,
	proxyBasenamePrefix,
	path,
	queryParam,
}) => {
	const alternates = shops.flatMap(shop => {
		const country = shop?.sellingCountry || "WW";
		const locales = Array.isArray(shop?.locales) ? shop.locales : [];

		return locales.map(locale => {
			const rawHreflang = `${locale}-${country}`;

			return {
				rel: "alternate",
				hreflang: formatHreflang(rawHreflang),
				href: getHref({
					brand,
					domain,
					proxyBasenamePrefix,
					locale,
					country,
					path,
					queryParam,
				}),
			};
		});
	});

	const hasEnWWShop = shops.some(
		shop =>
			(shop?.sellingCountry || "WW") === "WW" &&
			Array.isArray(shop?.locales) &&
			shop.locales.includes("en")
	);

	const withXDefault = hasEnWWShop
		? [
				...alternates,
				{
					rel: "alternate",
					hreflang: "x-default",
					href: getHref({
						brand,
						domain,
						proxyBasenamePrefix,
						locale: "en",
						country: "WW",
						path,
						queryParam,
					}),
				},
			]
		: alternates;

	return Array.from(new Map(withXDefault.map(item => [item.hreflang, item])).values());
};

const isKeyStringIntl = str => {
	const regex = /^[a-z0-9]+(\.[a-z0-9]+)+$/;
	return regex.test(str);
};

const getRobotsContent = ({ robotsMetaTag, isPreview, isLiveEnvironment }) => {
	if (isPreview || !isLiveEnvironment) {
		return "noindex, nofollow";
	}

	return robotsMetaTag || null;
};

const HeadMetadata = ({
	prefetchRessources = [],
	title,
	titleAttributes = {},
	robotsMetaTag = "",
	imageUrl = "",
	description,
	descriptionAttributes = {},
	children,
}) => {
	const brandDisplayName = useSelector(state => getBrandDisplayName(state));
	const shops = useSelector(state => state.brand.shops) || [];
	const intl = useIntl();

	const { brand, shop, envVars } = useContext(appGlobalsContext);

	const proxyBasenamePrefix = envVars.PROXY_BASENAME_PREFIX;

	const environment = envVars.ENVIRONMENT;

	const { pathname, search } = useLocation();

	const query = parseQueries(search);

	const isPreview = search.includes("preview=");

	const robotsContent = getRobotsContent({
		robotsMetaTag,
		isPreview,
		isLiveEnvironment: environment?.includes("live"),
	});

	const computedPathname = pathname === "/" ? "" : pathname;

	const queryParam =
		computedPathname === "/merch" && query.code
			? `?code=${encodeURIComponent(query.code)}`
			: "";

	const envVarBaseUrl = envVars.BASE_URL;

	// Pour Saudia, on force le domaine des canonical et alternate vers www.saudia.com
	const computedDomain = getDomain(envVarBaseUrl);

	const alternates = computeAlternates({
		path: computedPathname,
		domain: computedDomain,
		proxyBasenamePrefix,
		queryParam,
		shops,
		brand,
	});

	const rawTitle = title
		? isKeyStringIntl(title)
			? intl.formatMessage({ id: title }, { siteTitle: brandDisplayName, ...titleAttributes })
			: formatTemplate(title, { siteTitle: brandDisplayName, ...titleAttributes })
		: "";

	// category 0 means "no rating", but templates bake in " 0*"/" 0-star" literally right
	// after {name}; strip only past that point so a name containing "0*" stays untouched.
	const name = titleAttributes?.name ?? "";
	const nameEndIndex = rawTitle.indexOf(name) + name.length;

	const formattedTitle =
		titleAttributes?.category === 0
			? rawTitle.slice(0, nameEndIndex) +
				rawTitle.slice(nameEndIndex).replace(/\s+0(\*|-star)/, "")
			: rawTitle;

	return (
		<Helmet>
			{title && <title>{formattedTitle}</title>}

			{title && <meta property="og:title" content={formattedTitle} />}

			{description && (
				<meta
					name="description"
					content={
						isKeyStringIntl(description)
							? intl.formatMessage(
									{ id: description },
									{ siteTitle: brandDisplayName, ...descriptionAttributes }
								)
							: formatTemplate(description, {
									siteTitle: brandDisplayName,
									...descriptionAttributes,
								})
					}
				/>
			)}

			{description && (
				<meta
					property="og:description"
					content={
						isKeyStringIntl(description)
							? intl.formatMessage(
									{ id: description },
									{ siteTitle: brandDisplayName, ...descriptionAttributes }
								)
							: formatTemplate(description, {
									siteTitle: brandDisplayName,
									...descriptionAttributes,
								})
					}
				/>
			)}

			{imageUrl && <meta property="og:image" content={imageUrl} />}

			{imageUrl && <meta name="thumbnail" content={imageUrl} />}

			{robotsContent && <meta name="robots" content={robotsContent} />}

			<link
				rel="canonical"
				href={getCanonicalUrl({
					brand,
					shop,
					baseUrl: envVarBaseUrl,
					proxyBasenamePrefix,
					pathname,
					queryParam,
				})}
			/>

			{alternates.map(({ hreflang, href }, index) => {
				return <link key={index} rel="alternate" hrefLang={hreflang} href={href} />;
			})}

			{prefetchRessources.map((resourceUrl, index) => {
				return (
					<link
						key={index}
						rel="preload"
						as="image"
						href={resourceUrl}
						fetchPriority="high"
					/>
				);
			})}

			{children}
		</Helmet>
	);
};

HeadMetadata.propTypes = {
	title: PropTypes.string,
	titleAttributes: PropTypes.object,
	robotsMetaTag: PropTypes.string,
	imageUrl: PropTypes.string,
	description: PropTypes.string,
	descriptionAttributes: PropTypes.object,
	prefetchRessources: PropTypes.arrayOf(PropTypes.string),
};

export default HeadMetadata;
